/**
 * Subscription Management Page — /subscription-management
 *
 * Five-tab dashboard:
 *   1. Overview       — plan, usage rings, renewal countdown, grace-period banner
 *   2. Seat Management — member table, invite link, seat usage bar
 *   3. Analytics      — 30-day usage charts (recharts)
 *   4. Plan History   — vertical timeline of plan changes
 *   5. Entitlements   — feature unlock matrix with upgrade CTAs
 */

import React, { useState, useCallback } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import {
  LayoutList,
  Users,
  BarChart3,
  Clock,
  Grid3X3,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Lock,
  Unlock,
  Zap,
  Crown,
  RefreshCw,
  Copy,
  ExternalLink,
  Trash2,
  Shield,
  CreditCard,
  TrendingUp,
  CalendarDays,
  Info,
  ArrowUpRight,
  Download,
  KeyRound,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from "recharts";

import Layout, { PageContainer, PageHeader } from "../components/ui/Layout";
import PlanBadge from "../components/subscription/PlanBadge";
import UsageRing from "../components/subscription/UsageRing";
import PlanTimeline from "../components/subscription/PlanTimeline";
import UpgradeModal, { type UpgradeModalProps } from "../components/subscription/UpgradeModal";
import HealthScore from "../components/subscription/HealthScore";
import { RenewalCard, UsageAlertsCard, SpendAnalyticsCard, AuditLogTab } from "../components/subscription/CriticalFeatures";
import { BillingContactCard, CostCenterCard, SSOConfigTab } from "../components/subscription/HighPriorityFeatures";
import { useSubscription, usePlanHistory, useUsageAnalytics, useSeats, useEntitlements } from "../hooks/useSubscription";
import { removeSeat, getInviteLink } from "../api/subscriptions";
import { createPortalSession } from "../api/billing";
import { exportSeatMembers, exportPlanHistory, exportUsageSnapshots } from "../lib/export";
import { cn } from "../lib/utils";

// shadcn/ui
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/shadcn/card";
import { Badge } from "../components/shadcn/badge";
import { Button } from "../components/shadcn/button";
import { Progress } from "../components/shadcn/progress";
import { Separator } from "../components/shadcn/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/shadcn/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/shadcn/table";
import { Skeleton } from "../components/shadcn/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/shadcn/tooltip";

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  active:   { label: "Active",          variant: "default"     },
  trialing: { label: "Trial",           variant: "secondary"   },
  past_due: { label: "Past Due",        variant: "destructive" },
  canceled: { label: "Canceled",        variant: "outline"     },
  none:     { label: "No Subscription", variant: "outline"     },
};

const ROLE_BADGE: Record<string, string> = {
  admin:  "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  member: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  viewer: "bg-slate-500/15 text-slate-700 dark:text-slate-300",
};

const CHART_COLORS = { repos: "#6366f1", members: "#f59e0b", scans: "#22c55e" };

// ── Helper ────────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtRelativeDate(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  const days = Math.ceil(diff / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} day${days === 1 ? "" : "s"}`;
}

// ── Forecasting helpers ──────────────────────────────────────────────────────
// Pure functions: no API calls, work entirely on data already fetched.

function linearSlope(values: number[]): number {
  const n = values.length;
  if (n < 2) return 0;
  const xMean = (n - 1) / 2;
  const yMean = values.reduce((s, v) => s + v, 0) / n;
  const num = values.reduce((s, v, i) => s + (i - xMean) * (v - yMean), 0);
  const den = values.reduce((s, _, i) => s + Math.pow(i - xMean, 2), 0);
  return den === 0 ? 0 : num / den;
}

type ForecastResult =
  | { status: "growing"; daysLeft: number; label: string; color: string }
  | { status: "stable";  label: string; color: string }
  | { status: "at_limit"; label: string; color: string }
  | { status: "insufficient_data"; label: string; color: string }
  | { status: "unlimited"; label: string; color: string };

function forecastMetric(
  snapshots: number[],
  current: number,
  limit: number
): ForecastResult {
  if (limit === -1) return { status: "unlimited", label: "Unlimited", color: "#22c55e" };
  if (current >= limit) return { status: "at_limit", label: "Limit reached", color: "#ef4444" };
  if (snapshots.length < 5)
    return { status: "insufficient_data", label: "Collecting data…", color: "#94a3b8" };

  const slope = linearSlope(snapshots.slice(-14));
  if (slope <= 0.05) return { status: "stable", label: "Stable", color: "#22c55e" };

  const daysLeft = Math.ceil((limit - current) / slope);
  const color = daysLeft < 7 ? "#ef4444" : daysLeft < 30 ? "#f97316" : "#22c55e";
  return {
    status: "growing",
    daysLeft,
    label: `~${daysLeft} day${daysLeft === 1 ? "" : "s"} to limit`,
    color,
  };
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function SubscriptionManagement() {
  const [activeTab, setActiveTab] = useState("overview");
  const [upgradeModal, setUpgradeModal] = useState<Omit<UpgradeModalProps, "onClose"> | null>(null);
  const { plan } = useSubscription();

  const openUpgrade = useCallback((opts: Omit<UpgradeModalProps, "onClose">) => {
    setUpgradeModal(opts);
  }, []);

  return (
    <Layout>
      <PageContainer>
        <PageHeader
          title="Subscription Management"
          description="Manage your plan, seats, usage analytics, and feature entitlements"
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 h-auto p-1 bg-secondary/60">
            {[
              { id: "overview",    label: "Overview",   icon: LayoutList   },
              { id: "seats",       label: "Seats",      icon: Users        },
              { id: "analytics",   label: "Analytics",  icon: BarChart3    },
              { id: "history",     label: "History",    icon: Clock        },
              { id: "entitlements",label: "Features",   icon: Grid3X3      },
              { id: "audit",       label: "Audit Log",  icon: Shield       },
              { id: "security",    label: "Security",   icon: KeyRound     },
            ].map(({ id, label, icon: Icon }) => (
              <TabsTrigger
                key={id}
                value={id}
                className="flex items-center gap-1.5 text-xs font-medium py-2 data-[state=active]:shadow-sm"
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="space-y-0">
            <OverviewTab openUpgrade={openUpgrade} />
          </TabsContent>

          <TabsContent value="seats" className="space-y-0">
            <SeatsTab openUpgrade={openUpgrade} />
          </TabsContent>

          <TabsContent value="analytics" className="space-y-0">
            <AnalyticsTab />
          </TabsContent>

          <TabsContent value="history" className="space-y-0">
            <HistoryTab />
          </TabsContent>

          <TabsContent value="entitlements" className="space-y-0">
            <EntitlementsTab openUpgrade={openUpgrade} />
          </TabsContent>

          <TabsContent value="audit" className="space-y-0">
            <AuditLogTab />
          </TabsContent>

          <TabsContent value="security" className="space-y-0">
            <SSOConfigTab
              onUpgradeRequired={() =>
                openUpgrade({
                  featureId: "sso_saml",
                  featureLabel: "SAML Single Sign-On",
                  requiredPlan: "enterprise",
                  currentPlan: plan,
                })
              }
            />
          </TabsContent>
        </Tabs>
      </PageContainer>

      {/* Upgrade Modal — portal-rendered, only within this module */}
      {upgradeModal && (
        <UpgradeModal {...upgradeModal} onClose={() => setUpgradeModal(null)} />
      )}
    </Layout>
  );
}

// ── Tab 1: Overview ────────────────────────────────────────────────────────────

function OverviewTab({ openUpgrade }: { openUpgrade: (o: Omit<UpgradeModalProps, "onClose">) => void }) {
  const { overview, isLoading, plan } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch { setPortalLoading(false); }
  };

  if (isLoading) return <OverviewSkeleton />;

  if (!overview) {
    return (
      <div className="rounded-xl border border-border/60 p-12 text-center">
        <AlertTriangle className="h-8 w-8 text-muted-foreground/50 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Could not load subscription data.</p>
      </div>
    );
  }

  const { subscription, usage, grace } = overview;
  const statusInfo = STATUS_BADGE[subscription.status] ?? STATUS_BADGE.none;
  const renewalDate = subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd) : null;

  return (
    <div className="space-y-6">
      {/* Grace period warning */}
      {grace.in_grace_period && (
        <Card className="border-l-4 border-l-amber-500 bg-amber-500/5">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                Downgrade retention period active
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Your premium data will be retained for{" "}
                <span className="font-semibold text-amber-600 dark:text-amber-400">
                  {grace.days_remaining} more day{grace.days_remaining !== 1 ? "s" : ""}
                </span>{" "}
                (until {fmtDate(grace.grace_ends_at!)}). Upgrade before then to avoid data loss.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => openUpgrade({
                featureId: "premium_data_retention",
                featureLabel: "Premium Data Retention",
                requiredPlan: "starter",
                currentPlan: plan,
              })}
              className="flex-shrink-0"
            >
              Upgrade
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Past due warning */}
      {subscription.status === "past_due" && (
        <Card className="border-l-4 border-l-red-500 bg-red-500/5">
          <CardContent className="flex items-center gap-3 p-4">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-600 dark:text-red-400">Payment failed</p>
              <p className="text-xs text-muted-foreground">Update your payment method to avoid service interruption.</p>
            </div>
            <Button size="sm" variant="destructive" onClick={handlePortal} disabled={portalLoading}>
              {portalLoading && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
              Update Payment
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Plan hero card */}
      <Card className="overflow-hidden">
        <div className="h-1 w-full bg-gradient-to-r from-emerald-500 via-emerald-500 to-pink-500" />
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-secondary">
                {plan === "enterprise" ? (
                  <Crown className="h-7 w-7 text-amber-400" />
                ) : plan === "starter" || plan === "growth" ? (
                  <Zap className="h-7 w-7 text-emerald-400" />
                ) : (
                  <Shield className="h-7 w-7 text-slate-400" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h2 className="text-2xl font-bold text-foreground capitalize">{plan} Plan</h2>
                  <PlanBadge plan={plan} size="sm" showLabel={false} />
                  <Badge variant={statusInfo.variant} className="text-[10px]">
                    {statusInfo.label}
                  </Badge>
                </div>
                {renewalDate && subscription.status === "active" && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {subscription.billingModel === "one_time"
                      ? "Access expires"
                      : subscription.cancelAtPeriodEnd ? "Cancels" : "Renews"}{" "}
                    <span className="font-medium text-foreground">
                      {fmtRelativeDate(renewalDate.toISOString())}
                    </span>{" "}
                    · {fmtDate(renewalDate.toISOString())}
                  </p>
                )}
                {subscription.billingEmail && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Billing email:{" "}
                    <span className="text-foreground/80">{subscription.billingEmail}</span>
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {subscription.hasCustomer && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePortal}
                  disabled={portalLoading}
                  className="gap-1.5"
                >
                  {portalLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />}
                  Manage Billing
                </Button>
              )}
              {plan === "free" && (
                <Button
                  size="sm"
                  className="gap-1.5"
                  onClick={() => openUpgrade({
                    featureId: "pro_plan",
                    featureLabel: "Starter Plan",
                    requiredPlan: "starter",
                    currentPlan: plan,
                  })}
                >
                  <ArrowUpRight className="h-3.5 w-3.5" />
                  Upgrade
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Health Score */}
      <HealthScore overview={overview} />

      {/* Usage rings */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            Current Usage
          </CardTitle>
          <CardDescription className="text-xs">
            Usage resets monthly for scans. Repos and members are your current totals.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-3 gap-6 justify-items-center">
            <UsageRing
              current={usage.repos.current}
              limit={usage.repos.limit}
              label="Repositories"
              size={110}
            />
            <UsageRing
              current={usage.members.current}
              limit={usage.members.limit}
              label="Team Members"
              size={110}
            />
            <UsageRing
              current={usage.scansThisMonth.current}
              limit={usage.scansThisMonth.limit}
              label="Scans this month"
              size={110}
            />
          </div>

          {/* Limit upgrade prompts */}
          {(usage.repos.limit !== -1 && usage.repos.current >= usage.repos.limit) && (
            <div className="mt-4 flex items-center justify-between rounded-lg bg-red-500/5 border border-red-500/20 px-4 py-2.5">
              <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                <AlertTriangle className="h-3.5 w-3.5" />
                Repository limit reached
              </div>
              <Button size="sm" variant="outline" className="text-xs h-7"
                onClick={() => openUpgrade({ featureId: "repos", featureLabel: "More Repositories", requiredPlan: "starter", currentPlan: plan })}>
                Upgrade
              </Button>
            </div>
          )}
          {(usage.members.limit !== -1 && usage.members.current >= usage.members.limit) && (
            <div className="mt-2 flex items-center justify-between rounded-lg bg-red-500/5 border border-red-500/20 px-4 py-2.5">
              <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                <AlertTriangle className="h-3.5 w-3.5" />
                Seat limit reached
              </div>
              <Button size="sm" variant="outline" className="text-xs h-7"
                onClick={() => openUpgrade({ featureId: "members", featureLabel: "More Seats", requiredPlan: "starter", currentPlan: plan })}>
                Upgrade
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan limits summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Plan Limits</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { label: "Repositories", value: overview.limits.maxRepos === -1 ? "Unlimited" : overview.limits.maxRepos },
              { label: "Scans / month", value: overview.limits.maxScansPerMonth === -1 ? "Unlimited" : overview.limits.maxScansPerMonth },
              { label: "Team members", value: overview.limits.maxMembers === -1 ? "Unlimited" : overview.limits.maxMembers },
              { label: "Features", value: `${overview.limits.features.length} included` },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-lg bg-secondary/60 p-3">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-lg font-bold text-foreground mt-0.5">{value}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Renewal Management */}
      <RenewalCard
        cancelAtPeriodEnd={subscription.cancelAtPeriodEnd}
        currentPeriodEnd={subscription.currentPeriodEnd ? String(subscription.currentPeriodEnd) : null}
        status={subscription.status}
        oneTime={subscription.billingModel === "one_time"}
        plan={plan}
      />

      {/* Usage Alert Thresholds */}
      <UsageAlertsCard />

      {/* Billing Contact */}
      <BillingContactCard />

      {/* Internal Billing Info — Cost Center, PO, etc. */}
      <CostCenterCard />
    </div>
  );
}

function OverviewSkeleton() {
  return (
    <div className="space-y-6">
      <Card><CardContent className="p-6"><div className="flex gap-4"><Skeleton className="h-14 w-14 rounded-xl" /><div className="space-y-2 flex-1"><Skeleton className="h-6 w-48" /><Skeleton className="h-4 w-32" /></div></div></CardContent></Card>
      <Card><CardContent className="p-6"><div className="grid grid-cols-3 gap-6"><Skeleton className="h-28 w-full rounded-full" /><Skeleton className="h-28 w-full rounded-full" /><Skeleton className="h-28 w-full rounded-full" /></div></CardContent></Card>
    </div>
  );
}

// ── Tab 2: Seat Management ────────────────────────────────────────────────────

function SeatsTab({ openUpgrade }: { openUpgrade: (o: Omit<UpgradeModalProps, "onClose">) => void }) {
  const qc = useQueryClient();
  const seatsQuery = useSeats();
  const { plan } = useSubscription();
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteExpiry, setInviteExpiry] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const removeMutation = useMutation({
    mutationFn: (userId: string) => removeSeat(userId),
    onMutate: (userId) => setRemovingId(userId),
    onSettled: () => {
      setRemovingId(null);
      qc.invalidateQueries({ queryKey: ["subscriptions", "seats"] });
    },
  });

  const handleGenerateInvite = async () => {
    setInviteLoading(true);
    try {
      const res = await getInviteLink();
      setInviteUrl(res.invite_url);
      setInviteExpiry(res.expires_at);
    } catch (err: any) {
      if (err?.status === 402) {
        openUpgrade({
          featureId: "members",
          featureLabel: "More Seats",
          requiredPlan: "starter",
          currentPlan: plan,
        });
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!inviteUrl) return;
    await navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const data = seatsQuery.data;
  const pct = data
    ? data.seats.limit === -1
      ? 0
      : Math.min(100, (data.seats.current / Math.max(data.seats.limit, 1)) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Seat usage bar */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Seat Usage</h3>
              {data && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  <span className="font-bold text-foreground">{data.seats.current}</span>
                  {data.seats.limit === -1 ? " / Unlimited" : ` / ${data.seats.limit}`} seats used
                </p>
              )}
            </div>
            {data && data.seats.limit !== -1 && (
              <span className={cn("text-sm font-semibold", pct >= 100 ? "text-red-500" : pct >= 80 ? "text-amber-500" : "text-muted-foreground")}>
                {Math.round(pct)}%
              </span>
            )}
          </div>
          {data && data.seats.limit !== -1 && (
            <Progress
              value={pct}
              className={cn("h-2", pct >= 100 ? "[&>div]:bg-red-500" : pct >= 80 ? "[&>div]:bg-amber-500" : "")}
            />
          )}
          {data && data.seats.limit !== -1 && data.seats.current >= data.seats.limit && (
            <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Seat limit reached. Upgrade to invite more members.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Invite link section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <ExternalLink className="h-4 w-4 text-muted-foreground" />
            Invite New Member
          </CardTitle>
          <CardDescription className="text-xs">
            Generate a share-a-link invite (valid 24 hours). Anyone with the link can join as a member via GitHub OAuth.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerateInvite}
              disabled={inviteLoading}
              className="gap-1.5"
            >
              {inviteLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Generate Link
            </Button>

            {inviteUrl && (
              <div className="flex-1 rounded-lg border border-border/60 bg-secondary/40 px-3 py-2 flex items-center justify-between gap-2 min-w-0">
                <code className="text-xs text-foreground/80 truncate flex-1">{inviteUrl}</code>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopy}>
                          {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{copied ? "Copied!" : "Copy link"}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            )}
          </div>
          {inviteExpiry && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />
              Expires {fmtDate(inviteExpiry)}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Member table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              Members
            </CardTitle>
            {data && data.members.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={() => exportSeatMembers(data.members, data.plan)}
              >
                <Download className="h-3 w-3" /> Export CSV
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {seatsQuery.isLoading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[11px] font-semibold uppercase">Member</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase">Role</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase">Joined</TableHead>
                  <TableHead className="text-right text-[11px] font-semibold uppercase">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.members ?? []).map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        {m.github_avatar_url ? (
                          <img src={m.github_avatar_url} alt={m.github_login} className="h-7 w-7 rounded-full" />
                        ) : (
                          <div className="h-7 w-7 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-500 text-xs font-bold">
                            {(m.github_name || m.github_login).charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-foreground leading-none">
                            {m.github_name || m.github_login}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">@{m.github_login}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={cn("px-2 py-0.5 rounded text-[10px] font-semibold uppercase", ROLE_BADGE[m.role])}>
                        {m.role}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {fmtDate(m.joined_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-red-500"
                        disabled={removingId === m.id}
                        onClick={() => { if (window.confirm(`Remove @${m.github_login} from this org?`)) removeMutation.mutate(m.id); }}
                      >
                        {removingId === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab 3: Analytics ──────────────────────────────────────────────────────────

function AnalyticsTab() {
  const analyticsQuery = useUsageAnalytics();
  const { overview } = useSubscription();
  const data = analyticsQuery.data;

  if (analyticsQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Card><CardContent className="p-6"><Skeleton className="h-48 w-full" /></CardContent></Card>
        <Card><CardContent className="p-6"><Skeleton className="h-48 w-full" /></CardContent></Card>
      </div>
    );
  }

  if (analyticsQuery.isError || !data) {
    return (
      <div className="rounded-xl border border-border/60 p-12 text-center">
        <AlertTriangle className="h-8 w-8 text-muted-foreground/50 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Could not load usage analytics.</p>
      </div>
    );
  }

  // A summary counter can legitimately be null (an aggregate over no rows), and
  // rendering one used to throw a TypeError that the error boundary caught by
  // replacing the entire page — tab bar included — so no other tab was reachable.
  const num = (v: number | null | undefined) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

  const hasSnapshots = (data.snapshots ?? []).length > 0;
  const chartData = (data.snapshots ?? []).map((s) => ({
    date: new Date(s.snapshot_date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    repos: s.repos_count,
    members: s.members_count,
    scans: s.scans_count,
  }));

  // Forecasting — computed from snapshot values already in hand
  const scanForecast  = forecastMetric(
    (data.snapshots ?? []).map((s) => num(s.scans_count)),
    overview?.usage.scansThisMonth.current ?? 0,
    overview?.usage.scansThisMonth.limit   ?? -1,
  );
  const repoForecast  = forecastMetric(
    (data.snapshots ?? []).map((s) => num(s.repos_count)),
    overview?.usage.repos.current ?? 0,
    overview?.usage.repos.limit   ?? -1,
  );
  const memberForecast = forecastMetric(
    (data.snapshots ?? []).map((s) => num(s.members_count)),
    overview?.usage.members.current ?? 0,
    overview?.usage.members.limit   ?? -1,
  );

  const orgSlug = overview?.org.slug ?? "org";

  return (
    <div className="space-y-6">
      {/* Summary stats + export */}
      <div className="flex items-center justify-between mb-0">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Usage Summary</h3>
        {hasSnapshots && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 text-xs"
            onClick={() => exportUsageSnapshots(data.snapshots, orgSlug)}
          >
            <Download className="h-3 w-3" /> Export CSV
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Total Repos",      value: num(data.summary?.totalRepos),     icon: BarChart3  },
          { label: "Total Members",    value: num(data.summary?.totalMembers),   icon: Users      },
          { label: "Scans This Month", value: num(data.summary?.scansThisMonth), icon: RefreshCw  },
          { label: "All-time Scans",   value: num(data.summary?.scansTotal),     icon: TrendingUp },
        ].map(({ label, value, icon: Icon }) => (
          <Card key={label}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{label}</span>
                <Icon className="h-4 w-4 text-muted-foreground/50" />
              </div>
              <p className="text-2xl font-bold text-foreground">{value.toLocaleString()}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      {!hasSnapshots ? (
        <Card>
          <CardContent className="p-10 text-center">
            <Info className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm font-semibold text-foreground">No chart data yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Usage snapshots are collected daily going forward. Data will appear here after the first snapshot.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Scans chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Daily Scans (30 days)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-border/50" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                  <RechartTooltip
                    contentStyle={{ backgroundColor: "var(--surface1)", border: "1px solid var(--border)", borderRadius: "8px", fontSize: "12px" }}
                    labelStyle={{ fontWeight: 600 }}
                  />
                  <Line type="monotone" dataKey="scans" stroke={CHART_COLORS.scans} strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Scans" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Repos + Members chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Repos & Members (30 days)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-border/50" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                  <RechartTooltip
                    contentStyle={{ backgroundColor: "var(--surface1)", border: "1px solid var(--border)", borderRadius: "8px", fontSize: "12px" }}
                    labelStyle={{ fontWeight: 600 }}
                  />
                  <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }} />
                  <Line type="monotone" dataKey="repos" stroke={CHART_COLORS.repos} strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Repos" />
                  <Line type="monotone" dataKey="members" stroke={CHART_COLORS.members} strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Members" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Peak day callout */}
          {data.summary?.peakDay && (
            <Card className="border-emerald-500/20 bg-emerald-500/5">
              <CardContent className="p-4 flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    Peak scan day: {fmtDate(data.summary.peakDay.date)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {num(data.summary.peakDay.scans).toLocaleString()} scans in one day
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── Usage Forecasting card ── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                Usage Forecast
              </CardTitle>
              <CardDescription className="text-xs">
                Linear trend projection based on the last 14 days of usage snapshots.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-border/40">
                {[
                  { label: "Scans / month",  forecast: scanForecast,   icon: RefreshCw },
                  { label: "Repositories",   forecast: repoForecast,   icon: BarChart3 },
                  { label: "Team Members",   forecast: memberForecast, icon: Users     },
                ].map(({ label, forecast, icon: Icon }) => (
                  <div key={label} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-foreground">{label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Pill indicator */}
                      <span
                        className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full"
                        style={{
                          backgroundColor: forecast.color + "22",
                          color: forecast.color,
                        }}
                      >
                        {forecast.label}
                      </span>
                      {/* Days-left mini ring for "growing" status */}
                      {forecast.status === "growing" && (
                        <svg width="24" height="24" viewBox="0 0 24 24" className="-rotate-90 flex-shrink-0">
                          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="3" className="text-secondary" />
                          <circle
                            cx="12" cy="12" r="9" fill="none"
                            stroke={forecast.color} strokeWidth="3" strokeLinecap="round"
                            strokeDasharray={56.5}
                            strokeDashoffset={56.5 * (1 - Math.min(1, 30 / (forecast.daysLeft ?? 30)))}
                            style={{ transition: "stroke-dashoffset 0.8s ease" }}
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Spend Analytics — shown regardless of snapshot data */}
      <SpendAnalyticsCard />
    </div>
  );
}

// ── Tab 4: Plan History ───────────────────────────────────────────────────────


function HistoryTab() {
  const historyQuery = usePlanHistory();
  // Both hooks must run on every render. useSubscription() used to be called
  // *after* the isLoading early-return below, so the hook order changed the
  // moment the query settled and React threw, taking the whole page down with it.
  const { overview } = useSubscription();

  if (historyQuery.isLoading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
        </CardContent>
      </Card>
    );
  }

  const history = historyQuery.data ?? [];
  const orgSlug = overview?.org.slug ?? "org";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                Plan Change Timeline
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                All plan transitions for your organization, newest first.
              </CardDescription>
            </div>
            {history.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 text-xs flex-shrink-0"
                onClick={() => exportPlanHistory(history, orgSlug)}
              >
                <Download className="h-3 w-3" /> Export CSV
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <PlanTimeline history={history} />
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab 5: Entitlements ───────────────────────────────────────────────────────

function EntitlementsTab({ openUpgrade }: { openUpgrade: (o: Omit<UpgradeModalProps, "onClose">) => void }) {
  const entitlementsQuery = useEntitlements();
  const data = entitlementsQuery.data;

  if (entitlementsQuery.isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-xl border border-border/60 p-12 text-center">
        <AlertTriangle className="h-8 w-8 text-muted-foreground/50 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Could not load entitlement data.</p>
      </div>
    );
  }

  const unlocked = data.matrix.filter((f) => f.unlocked);
  const locked = data.matrix.filter((f) => !f.unlocked);

  return (
    <div className="space-y-6">
      {/* Counter */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-500/15 text-green-700 dark:text-green-400 flex items-center gap-1">
          <Unlock className="h-3 w-3" /> {unlocked.length} unlocked
        </span>
        {locked.length > 0 && (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-500/10 text-muted-foreground flex items-center gap-1">
            <Lock className="h-3 w-3" /> {locked.length} locked
          </span>
        )}
      </div>

      {/* Unlocked features */}
      {unlocked.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Included in your plan
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {unlocked.map((f) => (
              <div
                key={f.id}
                className="rounded-xl border border-green-500/20 bg-green-500/5 p-4 flex items-start gap-3"
              >
                <div className="p-1.5 rounded-lg bg-green-500/20 mt-0.5 flex-shrink-0">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground leading-snug">{f.label}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-wide">{f.id}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Separator />

      {/* Locked features */}
      {locked.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Available on higher plans
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {locked.map((f) => {
              const lowestPlan = f.available_on[0] ?? "starter";
              return (
                <div
                  key={f.id}
                  className="rounded-xl border border-border/60 bg-surface1/60 p-4 flex items-start justify-between gap-3 group hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-colors cursor-default"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 rounded-lg bg-secondary mt-0.5 flex-shrink-0">
                      <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground leading-snug">{f.label}</p>
                      <div className="flex items-center gap-1 mt-1">
                        {lowestPlan !== "enterprise" ? (
                          <span className="flex items-center gap-0.5 text-[10px] text-emerald-500 font-semibold">
                            <Zap className="h-2.5 w-2.5" /> {lowestPlan === "growth" ? "Growth+" : "Starter+"}
                          </span>
                        ) : (
                          <span className="flex items-center gap-0.5 text-[10px] text-amber-500 font-semibold">
                            <Crown className="h-2.5 w-2.5" /> Enterprise
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    onClick={() => openUpgrade({
                      featureId: f.id,
                      featureLabel: f.label,
                      requiredPlan: lowestPlan,
                      currentPlan: data.plan,
                    })}
                  >
                    Unlock
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {locked.length === 0 && (
        <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-8 text-center">
          <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-3" />
          <p className="text-sm font-semibold text-foreground">All features unlocked!</p>
          <p className="text-xs text-muted-foreground mt-1">Your Enterprise plan gives you access to every feature.</p>
        </div>
      )}
    </div>
  );
}
